import React from 'react';
import { Check, X, Loader2, Info } from 'lucide-react';
import { Button } from '../Button';
import { API_BASE_URL } from '../../utils/constants';
import type { SubscriptionPlan, BillingPeriod, UserSubscription } from '../../types';

interface PricingCardProps {
  plan: SubscriptionPlan;
  billingPeriod: BillingPeriod;
  isCurrentPlan: boolean;
  onAction: (plan: SubscriptionPlan) => void;
  loading?: boolean;
  currentPlanName?: string;
  plans: SubscriptionPlan[];
  isAdminOverride?: boolean;
}

export function PricingCard({
  plan,
  billingPeriod,
  isCurrentPlan,
  onAction,
  loading,
  currentPlanName,
  plans,
  isAdminOverride,
}: PricingCardProps) {
  const isPopular = plan.name === 'pro';
  const billedPriceCents = billingPeriod === 'annual'
    ? plan.display_annual_price_cents
    : plan.display_monthly_price_cents;
  const monthlyEquivalent = billingPeriod === 'annual'
    ? Math.round(plan.display_annual_price_cents / 12)
    : plan.display_monthly_price_cents;

  const formatPrice = (cents: number) =>
    new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: plan.currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);

  const getButtonText = () => {
    if (loading) return <Loader2 className="w-5 h-5 animate-spin mx-auto" />;

    // When not logged in, show "Get Started" for all plans
    if (!currentPlanName) {
      return 'Get Started';
    }

    if (isCurrentPlan) return isAdminOverride ? 'Assigned by Admin' : 'Current Plan';

    if (isAdminOverride) return 'Unavailable';

    if (currentPlanName === 'free') {
      return plan.name === 'free' ? 'Current Plan' : 'Upgrade';
    }

    const planIndex = plans.findIndex(p => p.name === plan.name);
    const currentIndex = plans.findIndex(p => p.name === currentPlanName);

    if (planIndex < currentIndex) return 'Downgrade';
    return 'Upgrade';
  };

  return (
    <div
      className={`bg-white rounded-xl p-8 relative flex flex-col ${
        isPopular
          ? 'border-2 border-[--clinqr-primary] shadow-lg'
          : 'border border-[--clinqr-border] shadow-sm'
      }`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[--clinqr-primary] text-white rounded-full text-xs font-medium">
          Most Popular
        </div>
      )}

      <div className="mb-2">
        <h3 className="text-[24px] font-semibold text-[--clinqr-text-primary]">
          {plan.display_name}
        </h3>
      </div>

      <div className="mb-2">
        <span className={`text-[48px] font-bold ${isPopular ? 'text-[--clinqr-primary]' : 'text-[--clinqr-text-primary]'}`}>
          {formatPrice(monthlyEquivalent)}
        </span>
        <span className="text-[20px] text-[--clinqr-text-secondary]">/month</span>
      </div>

      <p className="text-sm text-[--clinqr-text-secondary] mb-6 h-10">
        {plan.description}
      </p>

      <Button
        variant={isPopular ? 'primary' : 'secondary'}
        className={`w-full h-12 mb-6 transition-all duration-200 ${
          isPopular
            ? 'hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]'
            : 'active:scale-[0.98]'
        }`}
        onClick={() => onAction(plan)}
        disabled={isCurrentPlan || loading || isAdminOverride || (plan.name === 'free' && currentPlanName === 'free')}
      >
        {getButtonText()}
      </Button>

      <div className="h-px bg-[--clinqr-border] mb-6 opacity-20"></div>

      <ul className="space-y-4 flex-grow">
        <li className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-[--clinqr-navy] flex items-center justify-center flex-shrink-0">
            <Check className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={3} />
          </div>
          <span className="text-[--clinqr-text-primary] font-medium">
            {plan.qr_code_limit} Active QR Code{plan.qr_code_limit > 1 ? 's' : ''}
          </span>
        </li>
        <li className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-[--clinqr-navy] flex items-center justify-center flex-shrink-0">
            <Check className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={3} />
          </div>
          <span className="text-[--clinqr-text-primary] font-medium">
            {plan.monthly_scan_limit ? `${plan.monthly_scan_limit.toLocaleString()} scans/month` : 'Unlimited scans'}
          </span>
        </li>
        <li className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-[--clinqr-navy] flex items-center justify-center flex-shrink-0">
            <Check className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={3} />
          </div>
          <span className="text-[--clinqr-text-primary] font-medium">Time & Device Routing</span>
        </li>
        <li className="flex items-center gap-3">
          <div className="w-5 h-5 rounded-full bg-[--clinqr-navy] flex items-center justify-center flex-shrink-0">
            <Check className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={3} />
          </div>
          <span className="text-[--clinqr-text-primary] font-medium">A/B Testing</span>
        </li>

        {/* Conditional Features */}
        <FeatureItem available={plan.features.geographic_routing} text="Geographic Routing" />
        <FeatureItem
          available={plan.features.api_access}
          text="API Access"
          docsUrl={`${API_BASE_URL}/v1/docs`}
        />
        <FeatureItem
          available={plan.features.api_access}
          text="MCP Access"
          docsUrl={`${API_BASE_URL}/v1/mcp-docs`}
        />
        <FeatureItem available={plan.features.priority_support} text="Priority Support" />
      </ul>

      <p className="text-xs text-[--clinqr-text-secondary] text-center mt-6">
        {plan.name === 'free'
          ? 'No credit card needed'
          : `Billed ${formatPrice(billedPriceCents)} ${billingPeriod === 'annual' ? 'annually' : 'monthly'}`}
      </p>
    </div>
  );
}

function FeatureItem({
  available,
  text,
  docsUrl,
}: {
  available: boolean;
  text: string;
  docsUrl?: string;
}) {
  return (
    <li className="flex items-center gap-3">
      {available ? (
        <div className="w-5 h-5 rounded-full bg-[--clinqr-navy] flex items-center justify-center flex-shrink-0">
          <Check className="w-3.5 h-3.5 text-[--clinqr-lime]" strokeWidth={3} />
        </div>
      ) : (
        <div className="w-5 h-5 rounded-full bg-[--clinqr-border]/10 flex items-center justify-center flex-shrink-0">
          <X className="w-3.5 h-3.5 text-[--clinqr-border]" strokeWidth={3} />
        </div>
      )}
      <span className={available ? 'text-[--clinqr-text-primary] font-medium' : 'text-[--clinqr-text-muted]'}>
        {text}
      </span>
      {available && docsUrl && (
        <a
          href={docsUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${text} docs`}
          className="text-[--clinqr-text-secondary] hover:text-[--clinqr-primary] transition-colors"
        >
          <Info className="w-3.5 h-3.5" />
        </a>
      )}
    </li>
  );
}
